import { cn } from "@/utils";
import type React from "react";
import { useEffect, useState } from "react";

interface PDFViewerProps {
  file: string | File;
  width?: number | string;
  height?: number | string;
  className?: string;
  page?: number;
  toolbar?: 0 | 1;
  navpanes?: 0 | 1;
  scrollbar?: 0 | 1;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  file,
  width = "100%",
  height = "758px",
  className = "",
  page = 1,
  toolbar = 0,
  navpanes = 0,
  scrollbar = 0,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const loadPDF = () => {
      try {
        setLoading(true);
        setError("");

        if (typeof file === "string") {
          if (!cancelled) setPdfUrl(file);
        } else if (file instanceof File) {
          objectUrl = URL.createObjectURL(file);
          if (!cancelled) setPdfUrl(objectUrl);
        }

        if (!cancelled) setLoading(false);
      } catch (err) {
        console.error("Error loading PDF:", err);
        if (!cancelled) {
          setError("Can not load file PDF");
          setLoading(false);
        }
      }
    };

    if (file) {
      loadPDF();
    }

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading PDF...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center p-8 ${className}`}
      >
        <div className="text-red-500 text-lg mb-2">❌ Error</div>
        <p className="text-gray-600 mb-4">{error}</p>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <iframe
        src={`${pdfUrl}#page=${page}&toolbar=${toolbar}&navpanes=${navpanes}&scrollbar=${scrollbar}`}
        style={{ overflow: "hidden" }}
        width={width}
        height={height}
        className={cn(`shadow-lg border-0 h-[${height}]`)}
        title="PDF Viewer"
        onLoad={() => setLoading(false)}
        onError={() => setError("Can not display PDF")}
      />
    </div>
  );
};

export { PDFViewer };
